/**
 * The platform console (/admin) — schools.
 *
 * What this screen deliberately does NOT show: any student, paper, report or
 * result. A platform operator manages tenants; that gives them no view inside
 * one. Counts are aggregates, and even those stop at "how many".
 *
 * The one way inside is "Manage", for a super admin: the school's own console
 * as an audited support action, which the school sees in its activity log. See
 * SupportSchoolLayout.
 */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useClaims } from '@/hooks/useSession';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import { queryClient } from '@/lib/queryClient';
import { slugifyTenantName, isValidTenantSlug, SIGNUP_POLICIES } from '@cwy/config/contracts';
import type { Board, SignupPolicy } from '@cwy/config/contracts';
import { Button, Card, Field, Input, Select, StudentPage } from '@/components/ds';
import { AdminBlock, AdminHero } from '@/components/admin/AdminBlocks';
import { SecretReveal } from '@/components/admin/SecretReveal';
import { SchoolEditPanel } from '@/components/admin/SchoolEditPanel';
import { BoardPicker } from '@/components/admin/BoardPicker';

interface TenantRow {
  id: string;
  name: string;
  slug: string;
  type: 'FIRST_PARTY' | 'SCHOOL';
  status: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  signupPolicy: SignupPolicy;
  /** The boards CWY set it up to teach (`TenantBoard`). */
  boards: Board[];
  /** The academic year the school is running — schools start the next one themselves. */
  academicYear: { id: string; label: string } | null;
  createdAt: string;
  classroomCount: number;
  memberCount: number;
  roleCounts: Record<string, number>;
}

interface CreatedTenant {
  tenantId: string;
  slug: string;
  adminUserId: string;
  adminTemporaryPassword: string | null;
}

const POLICY_LABEL: Record<SignupPolicy, string> = {
  CLOSED: 'Sign-in only — staff create every account',
  ROSTER_ACTIVATION: 'Students activate a roster record with a code',
  INVITE_CODE: 'Students register with an invitation',
  OPEN_APPROVAL: 'Students request access, staff approve',
};

export function PlatformSchoolsPage() {
  const navigate = useNavigate();
  const superAdmin = useClaims()?.platformRole === 'PLATFORM_SUPER_ADMIN';
  const tenants = useQuery({
    queryKey: ['platform', 'tenants'],
    queryFn: () => apiGet<TenantRow[]>('/platform/tenants'),
  });

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [policy, setPolicy] = useState<SignupPolicy>('CLOSED');
  const [boards, setBoards] = useState<Set<Board>>(new Set());
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [error, setError] = useState('');
  const [created, setCreated] = useState<{ result: CreatedTenant; email: string } | null>(null);
  /** Which school's edit panel is open. One at a time — this is a list, not a form. */
  const [editing, setEditing] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const createTenant = useMutation({
    mutationFn: () =>
      apiPost<CreatedTenant>('/platform/tenants', {
        name: name.trim(),
        slug: slug.trim(),
        signupPolicy: policy,
        boards: [...boards],
        adminName: adminName.trim(),
        adminEmail: adminEmail.trim(),
      }),
    onSuccess: (result) => {
      setCreated({ result, email: adminEmail.trim() });
      setShowForm(false);
      setName('');
      setSlug('');
      setSlugTouched(false);
      setBoards(new Set());
      setAdminName('');
      setAdminEmail('');
      void queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: TenantRow['status'] }) =>
      apiPatch(`/platform/tenants/${id}/status`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] }),
    onError: (err: Error) => setError(err.message),
  });

  // The slug is suggested from the name but stays editable, and stops being
  // suggested the moment it is edited by hand — a slug is permanent once the
  // school is live, so it should never be quietly rewritten under someone.
  const onNameChange = (value: string) => {
    setName(value);
    if (!slugTouched) setSlug(slugifyTenantName(value) ?? '');
  };

  const slugValid = slug.length === 0 || isValidTenantSlug(slug);
  const canSubmit = Boolean(
    name.trim().length >= 2 &&
    slugValid &&
    slug.length > 0 &&
    boards.size > 0 &&
    adminName.trim() &&
    adminEmail.trim(),
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (canSubmit) createTenant.mutate();
  };

  const schools = (tenants.data ?? []).filter((row) => row.type === 'SCHOOL');
  const visibleTenants = (tenants.data ?? []).filter((row) =>
    `${row.name} ${row.slug} ${row.boards.join(' ')}`
      .toLowerCase()
      .includes(search.trim().toLowerCase()),
  );
  const loaded = tenants.isSuccess;

  return (
    <StudentPage className="!max-w-none">
      <AdminHero
        eyebrow="CWY · Platform workspace"
        title="Your platform, at a glance."
        description="Build the curriculum, bring schools on board and give every classroom a strong place to start."
        action={
          <>
            <Button onClick={() => setShowForm((v) => !v)}>
              {showForm ? 'Cancel' : 'Add a school'}
            </Button>
            <Button variant="secondary" onClick={() => navigate('/admin/curriculum')}>
              Open curriculum <span aria-hidden="true">↗</span>
            </Button>
          </>
        }
      >
        <div className="rounded-card bg-slate-900 p-5 text-white">
          <p className="text-fs-11 font-bold uppercase tracking-widest text-lime">
            The bigger picture
          </p>
          <p className="mt-2 text-xl font-semibold leading-snug">
            One shared foundation.
            <br />
            Every school's own journey.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 text-fs-11 font-semibold">
            <span className="rounded-lg bg-bin-green-fill px-3 py-2 text-bin-green-deep">
              Curriculum
            </span>
            <span className="rounded-lg bg-bin-purple-fill px-3 py-2 text-bin-purple-deep">
              Content
            </span>
            <span className="rounded-lg bg-bin-orange-fill px-3 py-2 text-bin-orange-deep">
              Classrooms
            </span>
          </div>
        </div>
      </AdminHero>

      <section aria-label="Platform at a glance" className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <AdminBlock
          label="Schools"
          value={loaded ? schools.length : '—'}
          detail="School workspaces on CWY"
          tone="GREEN"
        />
        <AdminBlock
          label="Active schools"
          value={loaded ? schools.filter((row) => row.status === 'ACTIVE').length : '—'}
          detail="Ready for their teaching day"
          tone="PURPLE"
        />
        <AdminBlock
          label="Classrooms"
          value={loaded ? schools.reduce((sum, row) => sum + row.classroomCount, 0) : '—'}
          detail="Across school workspaces"
          tone="BLUE"
        />
        <AdminBlock
          label="Boards in use"
          value={loaded ? new Set(schools.flatMap((row) => row.boards)).size : '—'}
          detail="From the schools' assigned boards"
          tone="ORANGE"
        />
      </section>

      <section aria-labelledby="platform-tools-title">
        <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
          <h2 id="platform-tools-title" className="text-lg font-bold text-forest">
            Make room for learning
          </h2>
          <p className="text-xs text-ink-soft">Your everyday platform tools</p>
        </div>
        <div className={`grid gap-3 ${superAdmin ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}>
          <AdminBlock
            label="01 · Build the foundation"
            value="Curriculum"
            detail="Shape boards, grades, subjects and the concepts beneath them."
            tone="PURPLE"
            to="/admin/curriculum"
          />
          <AdminBlock
            label="02 · Prepare the practice"
            value="Past papers"
            detail="Review and publish board papers for the students who need them."
            tone="BLUE"
            to="/admin/papers"
          />
          {superAdmin && (
            <AdminBlock
              label="03 · Bring your people"
              value="Staff"
              detail="Manage platform operators and CWY's own teaching team."
              tone="PINK"
              to="/admin/staff"
            />
          )}
        </div>
      </section>

      {created?.result.adminTemporaryPassword && (
        <SecretReveal
          title="School created — give these to the school admin"
          identifier={created.email}
          secret={created.result.adminTemporaryPassword}
          onDismiss={() => setCreated(null)}
        />
      )}

      {error && <p className="text-sm text-danger">{error}</p>}

      {showForm && (
        <Card title="Add a school">
          <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
            <Field label="School name" required>
              {({ id }) => (
                <Input
                  id={id}
                  autoFocus
                  value={name}
                  maxLength={120}
                  placeholder="Delhi Public School, R.K. Puram"
                  onChange={(e) => onNameChange(e.target.value)}
                />
              )}
            </Field>

            <Field
              label="URL"
              hint={
                slug
                  ? `Students will sign in at /school/${slug}. This cannot be changed later.`
                  : 'Lowercase letters, numbers and hyphens. This cannot be changed later.'
              }
              error={!slugValid ? 'Use 2–63 lowercase letters, numbers and single hyphens.' : ''}
              required
            >
              {({ id, invalid }) => (
                <Input
                  id={id}
                  invalid={invalid}
                  value={slug}
                  maxLength={63}
                  placeholder="dps-rkp"
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(e.target.value.toLowerCase());
                  }}
                />
              )}
            </Field>

            <Field
              label="Boards"
              hint="The school gets every grade CWY teaches under each board you tick, and new topics CWY adds later arrive on their own. The school admin can untick topics they don't teach, but cannot change the boards."
              required
            >
              {() => <BoardPicker value={boards} onChange={setBoards} />}
            </Field>

            {/* Disabled, not removed: the field is what a school is created
                with, and only “sign-in only” has screens behind it today. */}
            <Field
              label="How students get accounts"
              hint="The school's staff create every account. The other policies are not available yet."
            >
              {({ id }) => (
                <Select
                  id={id}
                  disabled
                  value={policy}
                  onChange={(e) => setPolicy(e.target.value as SignupPolicy)}
                >
                  {SIGNUP_POLICIES.map((value) => (
                    <option key={value} value={value}>
                      {POLICY_LABEL[value]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <div className="border-t border-line pt-4">
              {/* Created together, deliberately: a school with no administrator
                  is a workspace nobody can run without a platform operator. */}
              <p className="mb-3 text-sm text-ink-soft">
                The school&rsquo;s first admin. They will get a temporary password to pass on, and
                can then add their own staff and students.
              </p>
              <div className="flex flex-wrap gap-3">
                <Field label="Admin name" className="min-w-[200px] flex-1" required>
                  {({ id }) => (
                    <Input
                      id={id}
                      value={adminName}
                      maxLength={120}
                      onChange={(e) => setAdminName(e.target.value)}
                    />
                  )}
                </Field>
                <Field label="Admin email" className="min-w-[240px] flex-1" required>
                  {({ id }) => (
                    <Input
                      id={id}
                      type="email"
                      value={adminEmail}
                      maxLength={255}
                      onChange={(e) => setAdminEmail(e.target.value)}
                    />
                  )}
                </Field>
              </div>
            </div>

            <div>
              <Button type="submit" disabled={!canSubmit || createTenant.isPending}>
                {createTenant.isPending ? 'Creating…' : 'Create school'}
              </Button>
            </div>
          </form>
        </Card>
      )}

      <section aria-labelledby="schools-title" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 id="schools-title" className="text-lg font-bold text-forest">
              Your school community
            </h2>
            <p className="mt-1 text-xs text-ink-soft">
              Manage workspaces and their setup from one place.
            </p>
          </div>
          <label className="flex w-full flex-col gap-1.5 text-xs font-semibold text-ink-soft sm:w-72">
            Find a workspace
            <Input
              type="search"
              placeholder="Search name, slug or board…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>
        </div>
        {tenants.isPending && (
          <p className="text-ink-soft" role="status">
            Loading schools…
          </p>
        )}
        {tenants.isError && (
          <p className="text-sm text-danger" role="alert">
            Could not load schools. Please refresh to try again.
          </p>
        )}
        {loaded && visibleTenants.length === 0 && (
          <Card>
            <p className="text-sm text-ink-soft">
              {search.trim()
                ? 'No workspaces match your search.'
                : 'Your first school starts here. Add a school to get going.'}
            </p>
          </Card>
        )}

        <div className="flex flex-col gap-2.5">
          {visibleTenants.map((tenant) => (
            <Card
              key={tenant.id}
              padding="18px 20px"
              style={{
                borderLeft: `4px solid var(--${tenant.type === 'FIRST_PARTY' ? 'purple' : 'green'}-border)`,
              }}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 max-w-full">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-base font-bold text-forest">{tenant.name}</h3>
                    {tenant.type === 'FIRST_PARTY' && (
                      <span className="rounded-full bg-bin-purple-bg px-2 py-0.5 text-fs-11 font-semibold text-bin-purple-text">
                        CWY direct
                      </span>
                    )}
                    {tenant.status !== 'ACTIVE' && (
                      <span
                        className="rounded-full px-2 py-0.5 text-fs-11 font-semibold"
                        style={{ background: 'var(--pink-bg)', color: 'var(--pink-text)' }}
                      >
                        {tenant.status === 'SUSPENDED' ? 'Suspended' : 'Archived'}
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5 text-xs leading-relaxed text-ink-soft">
                    /school/{tenant.slug}
                    {tenant.boards.length > 0 && ` · ${tenant.boards.join(' + ')}`}
                    {tenant.academicYear && ` · ${tenant.academicYear.label}`}
                    {/* Each count opens the matching Manage tab — the names
                      behind it, inside the school as audited support — only
                      where Manage itself is offered. */}
                    <SchoolCounts
                      tenant={tenant}
                      manageBase={
                        superAdmin && tenant.type === 'SCHOOL' && tenant.status === 'ACTIVE'
                          ? `/admin/schools/${tenant.slug}`
                          : null
                      }
                    />
                  </div>
                </div>

                {tenant.type === 'SCHOOL' && (
                  <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
                    {/* Staff, students, classes and curriculum inside the school,
                      as audited support. Super admin only — the server admits
                      no one else — and only while the school is active. */}
                    {superAdmin && tenant.status === 'ACTIVE' && (
                      <Button onClick={() => navigate(`/admin/schools/${tenant.slug}`)}>
                        Manage
                      </Button>
                    )}
                    {/* The school as its students see it — their sign-in page, in a
                      new tab. Not its admin console: that is Manage, above,
                      which keeps the platform menu and a way back. */}
                    <a
                      href={`/school/${tenant.slug}/login`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm font-semibold no-underline"
                      style={{ color: 'var(--text-accent)' }}
                    >
                      Student sign-in
                    </a>
                    <Button
                      variant="secondary"
                      onClick={() =>
                        setEditing((current) => (current === tenant.id ? null : tenant.id))
                      }
                    >
                      {editing === tenant.id ? 'Close' : 'Edit'}
                    </Button>
                    <Button
                      variant="secondary"
                      disabled={setStatus.isPending}
                      onClick={() => {
                        // Suspension locks every member out immediately and
                        // revokes their sessions. Worth confirming.
                        const next = tenant.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
                        const message =
                          next === 'SUSPENDED'
                            ? `Suspend ${tenant.name}? Everyone there is signed out immediately and the school URL stops working. Nothing is deleted.`
                            : `Reactivate ${tenant.name}?`;
                        if (window.confirm(message)) {
                          setStatus.mutate({ id: tenant.id, status: next });
                        }
                      }}
                    >
                      {tenant.status === 'ACTIVE' ? 'Suspend' : 'Reactivate'}
                    </Button>
                  </div>
                )}

                {/* CWY's own workspace: its classes live here, beside it, now that
                  Classrooms is not a platform menu entry of its own. */}
                {tenant.type === 'FIRST_PARTY' && (
                  <div className="flex flex-none items-center gap-2">
                    <Button variant="secondary" onClick={() => navigate('/admin/classrooms')}>
                      Classes
                    </Button>
                  </div>
                )}
              </div>

              {editing === tenant.id && (
                <div className="mt-3">
                  <SchoolEditPanel tenantId={tenant.id} onDone={() => setEditing(null)} />
                </div>
              )}
            </Card>
          ))}
        </div>
      </section>
    </StudentPage>
  );
}

/**
 * "400 students · 20 teachers · 2 admins · 12 classes", each a link into the
 * matching Manage tab when `manageBase` is given. Aggregates on this screen;
 * the names are behind the link, in the school's console, as audited support.
 */
function SchoolCounts({ tenant, manageBase }: { tenant: TenantRow; manageBase: string | null }) {
  const counts: { n: number; noun: string; tab: string }[] = [
    { n: tenant.roleCounts.STUDENT ?? 0, noun: 'students', tab: 'students' },
    { n: tenant.roleCounts.TEACHER ?? 0, noun: 'teachers', tab: 'staff' },
    { n: tenant.roleCounts.SCHOOL_ADMIN ?? 0, noun: 'admins', tab: 'staff' },
    { n: tenant.classroomCount, noun: 'classes', tab: 'classrooms' },
  ];
  return (
    <>
      {counts.map(({ n, noun, tab }) => (
        <span key={noun}>
          {' · '}
          {manageBase ? (
            <Link
              to={`${manageBase}/${tab}`}
              className="font-semibold no-underline"
              style={{ color: 'var(--text-accent)' }}
            >
              {n} {noun}
            </Link>
          ) : (
            `${n} ${noun}`
          )}
        </span>
      ))}
    </>
  );
}
